import React from 'react';

import { Box, PancakeStack } from '@betterment/samba-react';
import { Helmet } from 'react-helmet';

import { Notifications } from '~/b4b/features/notifications/Notifications';
import { useCurrentEmployerId } from '~/b4b/utils/currentEmployer';

import Footer from './footer/Footer';
import Sidebar from './sidebar/Sidebar';

const MOBILE_HEADER_HEIGHT = '64px';
const SIDEBAR_WIDTH = '245px';

interface ApplicationLayoutProps {
  pageTitle: string;
}

/**
 * ApplicationLayout component provides the standard B4B layout structure
 * with sidebar navigation and content area.
 *
 * Usage:
 * <ApplicationLayout pageTitle="My Page">
 *   <ApplicationLayout.Header>
 *     <H1>Page Title</H1>
 *   </ApplicationLayout.Header>
 *   <ApplicationLayout.Content>
 *     Page content here
 *   </ApplicationLayout.Content>
 *   <ApplicationLayout.Footer />
 * </ApplicationLayout>
 */
const Layout: React.FC<React.PropsWithChildren<ApplicationLayoutProps>> = ({ pageTitle, children }) => {
  const employerId = useCurrentEmployerId();

  return (
    <Box
      css={{
        display: 'flex',
        width: '100vw',
        height: '100dvh',
        overflow: 'hidden',
      }}
    >
      <Helmet titleTemplate="%s | Betterment at Work" defaultTitle="Betterment at Work">
        <title>{pageTitle}</title>
      </Helmet>

      {/* Sidebar */}
      <Sidebar employerId={employerId} />

      {/* Main content area */}
      <PancakeStack
        css={{
          // Account for mobile header height
          marginTop: { base: MOBILE_HEADER_HEIGHT, lg: 0 },
          // On desktop, account for sidebar width
          width: {
            base: '100%',
            lg: `calc(100% - ${SIDEBAR_WIDTH})`,
          },
          background: 'background',
          maxWidth: 'none',
          height: '100%',
          overflowY: 'auto',
        }}
      >
        {children}
      </PancakeStack>
    </Box>
  );
};

const Header: React.FC<React.PropsWithChildren> = ({ children }) => (
  <PancakeStack.Header
    css={{
      maxWidth: 'auto',
      width: '100%',
      p: 'space.400',
    }}
  >
    <Box css={{ maxWidth: '1200px', mx: 'auto' }}>{children}</Box>
  </PancakeStack.Header>
);

const Content: React.FC<React.PropsWithChildren> = ({ children }) => (
  <PancakeStack.Main css={{ maxWidth: 'auto', width: '100%', p: 'space.400', pt: 'space.0' }}>
    <Box css={{ maxWidth: '1200px', mx: 'auto' }}>
      <Notifications css={{ paddingBottom: 'space.200' }} />
      {children}
    </Box>
  </PancakeStack.Main>
);

const LayoutFooter: React.FC = () => (
  <PancakeStack.Footer>
    <Footer />
  </PancakeStack.Footer>
);

type ApplicationLayoutComposite = typeof Layout & {
  Header: typeof Header;
  Content: typeof Content;
  Footer: typeof LayoutFooter;
};

export const ApplicationLayout = Layout as ApplicationLayoutComposite;
ApplicationLayout.Header = Header;
ApplicationLayout.Content = Content;
ApplicationLayout.Footer = LayoutFooter;
